import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex, VStack } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { KText } from '@/ui-library/components/text';
import { CoachMarkdown } from './CoachMarkdown';
import { composeQuery, isTerminal, splitQuery, type SeekMoment } from './api';
import {
  useCoachMessagePoll,
  useCoachMessages,
  useRetryCoachMessage,
  useSendCoachMessage,
} from './hooks';
import './coach.scss';

const Sparkle = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    {...p}
  >
    <path d="M12 3l1.9 4.6L18.5 9.5 14 11.4 12 16l-2-4.6L5.5 9.5 10 7.6z" />
    <circle cx="18.5" cy="5" r="1.2" fill="currentColor" />
    <circle cx="5.5" cy="17" r="1" fill="currentColor" />
  </svg>
);
const SendIcon = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    {...p}
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);
const Close = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    {...p}
  >
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);
const Chevron = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    {...p}
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
);

// Starter prompts shown before the coach has replied; once it has, the reply's own contextual
// follow-ups (message.followups) take their place.
const STARTERS = [
  'Why is that the big one?',
  'What did I miss in coverage?',
  'Draft follow-ups for the claims I let pass',
];

const buildWelcome = (bigOneTitle?: string | null) =>
  bigOneTitle
    ? `I've reviewed this interview — the note on the left has the full read. The biggest thing to work on: **${bigOneTitle}**.\n\n**Highlight anything** in the note to ask about it, click a coverage area, or use a shortcut below. 👋`
    : "I've reviewed this interview — the note on the left has the full read.\n\n**Highlight anything** in the note to ask about it, or use a shortcut below. 👋";

interface Props {
  interviewId: number | string;
  bigOneTitle?: string | null;
  pendingContext?: string | null;
  onContextConsumed?: () => void;
  onCollapse?: () => void;
  // When set, [mm:ss] timestamps the coach cites in its replies become clickable (open the player).
  onSeekMoment?: SeekMoment;
}

const UserBubble = ({ query }: { query: string }) => {
  const { context, text } = splitQuery(query);
  return (
    <Flex direction="column" align="flex-end" gap="5px">
      {context && <Box className="coach-bubble-ctx">“{context}”</Box>}
      <Box className="coach-bubble-user">{text}</Box>
    </Flex>
  );
};

const AgentBubble = ({ children }: { children: React.ReactNode }) => (
  <Flex gap="10px" align="flex-start">
    <Flex
      flex="none"
      align="center"
      justify="center"
      w="24px"
      h="24px"
      mt="1px"
      borderRadius="7px"
      color="primary-500"
      bg="primary-50"
    >
      <Sparkle width="14" height="14" />
    </Flex>
    <Box flex={1} minW={0}>
      {children}
    </Box>
  </Flex>
);

export function CoachCopilot({
  interviewId,
  bigOneTitle,
  pendingContext,
  onContextConsumed,
  onCollapse,
  onSeekMoment,
}: Props) {
  const qc = useQueryClient();
  const { data: messages = [] } = useCoachMessages(interviewId);
  const sendMut = useSendCoachMessage(interviewId);
  const retryMut = useRetryCoachMessage(interviewId);

  const [input, setInput] = useState('');
  const [optimistic, setOptimistic] = useState<string[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // The single in-flight message (if any) drives the live progress label and re-syncs the list.
  const inflight = useMemo(
    () =>
      messages.find((m) => m.status === 'pending' || m.status === 'processing'),
    [messages]
  );
  const poll = useCoachMessagePoll(interviewId, inflight?.id);
  const progress = inflight ? poll.data?.progress || 'Thinking…' : null;

  // Pills: the most recent reply's own contextual follow-ups; before any reply, the generic starters.
  const pills = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const f = messages[i].followups;
      if (messages[i].status === 'completed' && f && f.length) return f;
    }
    return STARTERS;
  }, [messages]);

  useEffect(() => {
    if (poll.data && isTerminal(poll.data.status)) {
      qc.invalidateQueries({ queryKey: ['coach-messages', interviewId] });
    }
  }, [poll.data?.status, poll.data, qc, interviewId]);

  // Drop optimistic bubbles once the persisted list contains them — derived at render time rather
  // than pruned in an effect, so there's no cascading re-render. The state array stays tiny (one
  // session of sends), so we never need to shrink it.
  const existingQueries = useMemo(
    () => new Set(messages.map((m) => m.query)),
    [messages]
  );
  const visibleOptimistic = optimistic.filter((q) => !existingQueries.has(q));

  useEffect(() => {
    scrollRef.current?.scrollTo?.({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages, visibleOptimistic.length, progress]);

  useEffect(() => {
    if (pendingContext) inputRef.current?.focus();
  }, [pendingContext]);

  const send = (text: string, ctx?: string | null) => {
    const clean = text.trim();
    if (!clean && !ctx) return;
    const query = composeQuery(clean, ctx);
    setOptimistic((o) => [...o, query]);
    setInput('');
    // The textarea auto-grows on input; reset it to one row so the next message starts compact.
    if (inputRef.current) inputRef.current.style.height = 'auto';
    onContextConsumed?.();
    sendMut.mutate(query);
  };

  const isEmpty = messages.length === 0 && visibleOptimistic.length === 0;

  return (
    <Flex
      className="coach-copilot"
      direction="column"
      h="100%"
      bg="secondary-25"
      minW={0}
    >
      {/* header */}
      <Flex
        align="center"
        gap="11px"
        px="18px"
        h="56px"
        flex="none"
        borderBottom="1px solid"
        borderColor="secondary-100"
        bg="base-white"
      >
        <Flex
          align="center"
          justify="center"
          w="32px"
          h="32px"
          borderRadius="9px"
          color="base-white"
          bgGradient="linear(135deg, primary-400, primary-600)"
          boxShadow="0 5px 14px -5px rgba(96,90,251,0.7)"
        >
          <Sparkle />
        </Flex>
        <Box flex={1} minW={0}>
          <KText size="sm-semi-bold" color="secondary-900" fontWeight="600">
            Coach
          </KText>
          <Flex align="center" gap="6px" mt="1px">
            <Box w="6px" h="6px" borderRadius="50%" bg="status-4" />
            <KText size="xs-regular" color="secondary-600" fontSize="11.5px">
              Grounded in this interview’s transcript
            </KText>
          </Flex>
        </Box>
        {onCollapse && (
          <Flex
            as="button"
            onClick={onCollapse}
            align="center"
            justify="center"
            w="30px"
            h="30px"
            borderRadius="8px"
            color="secondary-600"
            _hover={{ bg: 'secondary-50', color: 'secondary-900' }}
          >
            <Chevron />
          </Flex>
        )}
      </Flex>

      {/* messages */}
      <Box
        ref={scrollRef}
        className="coach-scroll"
        flex={1}
        overflowY="auto"
        px="18px"
        py="18px"
      >
        <VStack align="stretch" spacing="18px">
          {isEmpty && (
            <AgentBubble>
              <CoachMarkdown>{buildWelcome(bigOneTitle)}</CoachMarkdown>
            </AgentBubble>
          )}

          {messages.map((m) => (
            <Box key={m.id}>
              <UserBubble query={m.query} />
              {m.status === 'completed' && m.response && (
                <Box mt="18px">
                  <AgentBubble>
                    <CoachMarkdown onSeek={onSeekMoment}>
                      {m.response}
                    </CoachMarkdown>
                  </AgentBubble>
                </Box>
              )}
              {m.status === 'failed' && (
                <Box mt="18px">
                  <AgentBubble>
                    <Flex align="center" gap="10px">
                      <KText
                        size="sm-regular"
                        color="secondary-600"
                        fontSize="13.5px"
                      >
                        Something went wrong generating that reply.
                      </KText>
                      <Flex
                        as="button"
                        px="11px"
                        py="5px"
                        borderRadius="7px"
                        bg="primary-50"
                        color="primary-600"
                        _hover={{ bg: 'primary-100' }}
                        onClick={() =>
                          retryMut.mutate(m.id, {
                            onSuccess: () =>
                              qc.invalidateQueries({
                                queryKey: ['coach-messages', interviewId],
                              }),
                          })
                        }
                      >
                        <KText
                          size="xs-semi-bold"
                          color="primary-600"
                          fontSize="12px"
                          fontWeight="600"
                        >
                          Retry
                        </KText>
                      </Flex>
                    </Flex>
                  </AgentBubble>
                </Box>
              )}
            </Box>
          ))}

          {visibleOptimistic.map((q, i) => (
            <UserBubble key={`opt-${i}`} query={q} />
          ))}

          {progress && (
            <Flex gap="10px" align="center" pl="2px">
              <Box className="coach-spin" />
              <KText
                size="sm-regular"
                className="coach-progress-text"
                fontSize="13px"
              >
                {progress}
              </KText>
            </Flex>
          )}
        </VStack>
      </Box>

      {/* composer */}
      <Box
        flex="none"
        px="16px"
        pt="12px"
        pb="16px"
        borderTop="1px solid"
        borderColor="secondary-100"
        bg="base-white"
      >
        {pendingContext && (
          <Flex
            className="coach-reveal"
            align="flex-start"
            gap="10px"
            mb="9px"
            p="9px 12px"
            bg="secondary-25"
            border="1px solid"
            borderColor="secondary-100"
            borderLeft="2.5px solid"
            borderLeftColor="primary-400"
            borderRadius="9px"
          >
            <Box flex={1} minW={0}>
              <KText
                size="xs-bold"
                color="secondary-900"
                fontSize="10px"
                letterSpacing="0.06em"
                textTransform="uppercase"
                mb="3px"
              >
                Asking about
              </KText>
              <KText
                size="xs-regular"
                color="secondary-700"
                fontSize="12px"
                fontStyle="italic"
                noOfLines={2}
              >
                {pendingContext}
              </KText>
            </Box>
            <Flex
              as="button"
              onClick={onContextConsumed}
              color="secondary-500"
              _hover={{ color: 'secondary-900' }}
              pt="2px"
            >
              <Close />
            </Flex>
          </Flex>
        )}

        <Flex className="coach-chips" gap="8px" overflowX="auto" pb="11px">
          {pills.map((label, i) => (
            <Flex
              key={`${i}-${label}`}
              as="button"
              flex="none"
              align="center"
              gap="6px"
              px="12px"
              py="7px"
              borderRadius="20px"
              bg="primary-25"
              border="1px solid"
              borderColor="primary-200"
              transition="0.15s"
              _hover={{ borderColor: 'primary-400', bg: 'primary-50' }}
              // Keep any highlighted moment attached, so tapping a pill asks about the selection too.
              onClick={() => send(label, pendingContext)}
            >
              <Box flex="none" color="primary-400">
                <Sparkle width="12" height="12" />
              </Box>
              <KText
                size="xs-medium"
                color="primary-700"
                fontSize="12px"
                whiteSpace="nowrap"
                fontWeight="600"
              >
                {label}
              </KText>
            </Flex>
          ))}
        </Flex>

        <Flex
          align="flex-end"
          gap="9px"
          bg="base-white"
          border="1px solid"
          borderColor="secondary-200"
          borderRadius="13px"
          pl="14px"
          pr="7px"
          py="7px"
          transition="0.15s"
          _focusWithin={{
            borderColor: 'primary-400',
            boxShadow: '0 0 0 3px rgba(96,90,251,0.12)',
          }}
        >
          <Box
            as="textarea"
            ref={inputRef as any}
            flex={1}
            rows={1}
            value={input}
            placeholder={
              pendingContext
                ? 'What about this moment?'
                : 'Ask your coach about any moment in this interview…'
            }
            onChange={(e: any) => {
              setInput(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height =
                Math.min(e.target.scrollHeight, 120) + 'px';
            }}
            onKeyDown={(e: any) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send(input, pendingContext);
              }
            }}
            sx={{
              border: 'none',
              outline: 'none',
              resize: 'none',
              bg: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5',
              maxHeight: '120px',
              py: '5px',
              color: 'var(--kula-colors-secondary-900)',
              '::placeholder': { color: 'var(--kula-colors-secondary-500)' },
            }}
          />
          <Flex
            as="button"
            align="center"
            justify="center"
            flex="none"
            w="36px"
            h="36px"
            borderRadius="10px"
            color="base-white"
            bg="primary-500"
            opacity={!input.trim() && !pendingContext ? 0.45 : 1}
            cursor={!input.trim() && !pendingContext ? 'default' : 'pointer'}
            _hover={
              !input.trim() && !pendingContext ? {} : { bg: 'primary-600' }
            }
            boxShadow="0 6px 16px -7px rgba(96,90,251,0.7)"
            onClick={() => send(input, pendingContext)}
          >
            <SendIcon />
          </Flex>
        </Flex>
      </Box>
    </Flex>
  );
}

export default CoachCopilot;
